import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Menu, MenuItem } from '@mui/material';

import ButtonWithLoader from '../../ButtonWithLoader';
import PublishingPostDialog from '../PublishingPostDialog/PublishingPostDialog';
import ScheduleDialog from './ScheduleDialog';

function ScheduleAndPublishButton({
  publishedAt,
  askToPublishAt,
  errors,
  isFetching = false,
  size = 'large',
  onSave,
  onChange,
  disabled,
  fullWidth = false,
  post,
}: any) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [openPublishingDialog, setOpenPublishingDialog] = useState(false);
  const [action, setAction] = useState('publish');
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  const handleClickOnButton = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseButton = () => {
    setAnchorEl(null);
    setRedirectTo(null);
  };

  const handleClickOnPublishNow = () => {
    setAnchorEl(null);
    setAction('publish');
    setOpenPublishingDialog(true);
    setRedirectTo('?modal=post-created');
  };

  const handleClickOpenEditDialog = () => {
    setAnchorEl(null);
    setOpenScheduleDialog(true);
  };

  const handleClickOpenCancelDialog = (selectedAction: any) => {
    setAnchorEl(null);
    setOpenPublishingDialog(true);
    setAction(selectedAction);
  };

  const handleCloseEditDialog = () => {
    setOpenScheduleDialog(false);
  };

  const handleClosePublishingDialog = () => {
    setOpenPublishingDialog(false);
    setRedirectTo(null);
  };

  const getButtonLabel = () => {
    if (publishedAt) {
      return <FormattedMessage id="POSTS.PUBLISHED" />;
    }

    if (askToPublishAt) {
      return <FormattedMessage id="POSTS.SCHEDULED" />;
    }

    return <FormattedMessage id="BUTTONS.PUBLISH" />;
  };

  const menuProps = {
    id: 'publish-menu',
    anchorEl,
    open: Boolean(anchorEl),
    onClose: handleCloseButton,
  };

  const unpublishButton = (
    <ButtonWithLoader
      isFetching={isFetching}
      fullWidth={fullWidth}
      disabled={disabled || isFetching}
      size={size}
      color="primary"
      variant="outlined"
      onClick={() => handleClickOpenCancelDialog('unpublish')}
      data-testid="post-form-action-unpublish"
    >
      <FormattedMessage id="PUBLISHING_POST_DIALOG.UNPUBLISH.BUTTON" />
    </ButtonWithLoader>
  );

  const publishButton = (
    <ButtonWithLoader
      isFetching={isFetching}
      fullWidth={fullWidth}
      disabled={disabled || isFetching}
      size={size}
      color="primary"
      variant={!publishedAt && askToPublishAt ? 'outlined' : 'contained'}
      onClick={handleClickOnButton}
      data-testid="post-form-action-schedule-menu"
      endIcon={<KeyboardArrowDownIcon color="inherit" />}
    >
      {getButtonLabel()}
    </ButtonWithLoader>
  );

  const scheduledMenuOptions = (
    <Menu {...menuProps}>
      <MenuItem onClick={handleClickOpenEditDialog} data-testid="post-form-action-publish-edit">
        <FormattedMessage id="POSTS.PUBLISH_EDIT" key="POSTS.PUBLISH_EDIT" />
      </MenuItem>
      <MenuItem
        onClick={() => handleClickOpenCancelDialog('unschedule')}
        key="PUBLISHING_POST_DIALOG.UNSCHEDULE.BUTTON"
        data-testid="post-form-action-cancel-scheduled"
      >
        <FormattedMessage id="PUBLISHING_POST_DIALOG.UNSCHEDULE.BUTTON" />
      </MenuItem>
      <MenuItem
        onClick={handleClickOnPublishNow}
        key="PUBLISHING_POST_DIALOG.PUBLISH.BUTTON"
        data-testid="post-form-action-publish-now"
      >
        <FormattedMessage id="PUBLISHING_POST_DIALOG.PUBLISH.BUTTON" />
      </MenuItem>
    </Menu>
  );

  const defaultMenuOptions = (
    <Menu {...menuProps}>
      {/* { Publish } */}
      <MenuItem
        onClick={handleClickOnPublishNow}
        key="PUBLISHING_POST_DIALOG.PUBLISH.BUTTON"
        data-testid="post-form-action-publish-now"
      >
        <FormattedMessage id="PUBLISHING_POST_DIALOG.PUBLISH.BUTTON" />
      </MenuItem>
      {/* { Schedule post } */}
      <MenuItem
        onClick={handleClickOpenEditDialog}
        key="PUBLISHING_POST_DIALOG.SCHEDULE.BUTTON"
        data-testid="post-form-action-publish-scheduled"
      >
        <FormattedMessage id="PUBLISHING_POST_DIALOG.SCHEDULE.BUTTON" />
      </MenuItem>
    </Menu>
  );

  return (
    <>
      {publishedAt && unpublishButton}
      {!publishedAt && publishButton}

      {!publishedAt && askToPublishAt && scheduledMenuOptions}
      {!publishedAt && !askToPublishAt && defaultMenuOptions}

      <ScheduleDialog
        open={openScheduleDialog}
        onCloseDialog={handleCloseEditDialog}
        onSave={onSave}
        errors={errors}
        askToPublishAt={askToPublishAt}
        onChange={onChange}
      />

      <PublishingPostDialog
        action={action}
        publishedAt={publishedAt}
        open={openPublishingDialog}
        onClose={handleClosePublishingDialog}
        redirectTo={redirectTo}
        post={post}
      />
    </>
  );
}

export default injectIntl(ScheduleAndPublishButton);
